import type { FC, PropsWithChildren } from 'react';

import type { Account } from '@shared/products-types';
import type { ProductWithHelpers } from '@shared/products-core';
import {
    ActiveMenuItemProvider,
    createMenuItemBuilder,
    useActiveProduct,
    useSideMenuNavigation,
} from '@shared/products-core';
import { getStrictTitleAlignedWithGeorgeApp } from '@shared/products-general-service';
import { formatIBAN } from '@shared/formatting-utils';

import { ProductAvatar } from '../components/ProductAvatar';
import { LayoutStructuredInfo } from '../components/LayoutStructuredInfo';
import type { StripeProps } from './LayoutProps';
import { MainLayout } from './MainLayout';
import { LayoutWithSideMenu } from './LayoutWithSideMenu';

type Props = StripeProps & { strategies: Parameters<typeof createMenuItemBuilder>[0] };

export const ActiveProductLayout: FC<PropsWithChildren<Props>> = ({
    strategies,
    stripeHeader,
    children,
}) => {
    const activeProduct = useActiveProduct<ProductWithHelpers<Account>>();
    const navigationItems = createMenuItemBuilder(strategies)(activeProduct);
    const active = useSideMenuNavigation(navigationItems);

    return (
        <ActiveMenuItemProvider value={active}>
            <MainLayout navigationItems={navigationItems} stripeHeader={stripeHeader}>
                <LayoutWithSideMenu
                    navigationItems={navigationItems}
                    layoutProductInfo={
                        <LayoutStructuredInfo
                            mainAmount={activeProduct?.mainAmount}
                            avatar={
                                <ProductAvatar
                                    product={activeProduct}
                                    hasColorPreview
                                    data-cy="stripe-product-info-icon"
                                />
                            }
                            alias={getStrictTitleAlignedWithGeorgeApp(activeProduct) || ''}
                            productType={activeProduct?.description || ''}
                            productIdentifier={formatIBAN(activeProduct.accountno.iban)}
                            data-cy="layout-product-info"
                        />
                    }
                >
                    {children}
                </LayoutWithSideMenu>
            </MainLayout>
        </ActiveMenuItemProvider>
    );
};
